import React from "react"
import clsx from "clsx"

import {
	Avatar,
	Button,
	CircularProgress,
	DialogContent,
	DialogTitle,
	Grid,
	Radio,
	RadioGroup,
	Paper,
	FormControlLabel,
	Typography
} from "@material-ui/core"
import { Check } from "@material-ui/icons"

import { letalkLinks } from "@/utils/link"
import { convertCentsInReals } from "@/utils/currencyFormatter"

import {
	PlanAddons,
	UpsellQuantityInfo,
	UpsellQuantityType
} from "@/protocols/planAddons"
import { AddonLabel, UpsellDetails } from "@/protocols/upsell"

import useStyles from "@/components/UpsellDialog/UpsellQuantityDialog/styles"

type UpsellQuantityDialogIdleContentProps = {
	upsellDetails: UpsellDetails
	isUserReachSubscriptionLimit: boolean
	upsellQuantityType: UpsellQuantityType
	planAddons: PlanAddons<"quantity">
	selectedUpsellPlanAddon: UpsellQuantityInfo
	isCreatePlanAddonLoading: boolean
	onActivateClick: () => void
	onCancelClick: () => void
	onSelectPlanAddon: (planAddonId: number) => void
}

const UpsellQuantityDialogIdleContent: React.FC<UpsellQuantityDialogIdleContentProps> = (props) => {
	const {
		planAddons,
		selectedUpsellPlanAddon,
		isCreatePlanAddonLoading,
		isUserReachSubscriptionLimit,
		upsellQuantityType,
		onActivateClick,
		onCancelClick,
		onSelectPlanAddon
	} = props

	const classes = useStyles()

	const getUpsellContent = () => {
		const upsellQuantityTypeToContent: Record<UpsellQuantityType, {
			title: string
			description: string
			benefits: React.ReactNode[]
			confirmButtonTitle: string
			cancelButtonTitle: string
			selectText: string
			termsFragment: string
			formatAddonLabel: (addon: UpsellQuantityInfo) => AddonLabel
		}> = {
			user: {
				title: isUserReachSubscriptionLimit ? "Seu limite de atendentes foi atingindo" : "Antecipe suas necessidades de atendimento",
				description: isUserReachSubscriptionLimit ? "Você atingiu o limite de atendentes simultâneos. Expanda sua capacidade de atendimento e melhore a experiência dos seus clientes. Adicione mais atendentes agora e otimize sua operação!"
					: "Evite imprevistos e contrate mais atendentes agora mesmo. Garanta que sua operação continue fluindo perfeitamente. Adicione mais atendentes agora e otimize sua operação!",
				benefits: [
					<Typography variant="body1" key={1}><strong>Atenda mais clientes</strong> e reduza o tempo de espera.</Typography>,
					<Typography variant="body1" key={2}><strong>Garanta agilidade</strong> com uma equipe reforçada.</Typography>,
					<Typography variant="body1" key={3}><strong>Aumente a produtividade</strong> com mais recursos disponíveis.</Typography>,
					<Typography variant="body1" key={4}><strong>Adapte-se rapidamente</strong> às demandas do seu negócio.</Typography>
				],
				confirmButtonTitle: "ATIVAR ATENDENTES AGORA",
				cancelButtonTitle: "Não tenho interesse agora",
				selectText: "Selecione a quantidade de atendentes:",
				termsFragment: `do adicional de ${selectedUpsellPlanAddon.quantity} atendente(s) por ${convertCentsInReals(selectedUpsellPlanAddon.priceInCents)}`,
				formatAddonLabel: (addon) => {
					return {
						quantityLabel: `${addon.quantity} atendente(s)`
					}
				}
			},
			voice_call_minutes: {
				title: "Seu limite de créditos de ligações foi atingido",
				description: "Você atingiu o limite de créditos de ligação do seu plano. Garanta que suas chamadas continuem fluindo sem interrupções. Adicione mais créditos agora e mantenha o atendimento por voz sempre disponível!",
				benefits: [
					<Typography variant="body1" key={1}><strong>Continue ligando</strong> sem interrupções no atendimento.</Typography>,
					<Typography variant="body1" key={2}><strong>Evite chamadas perdidas</strong> por falta de créditos disponíveis.</Typography>,
					<Typography variant="body1" key={3}><strong>Melhore a experiência do cliente</strong> com ligações sempre ativas.</Typography>,
					<Typography variant="body1" key={4}><strong>Escale seu atendimento por voz</strong> conforme a demanda cresce.</Typography>
				],
				confirmButtonTitle: "ATIVAR MAIS CRÉDITOS AGORA",
				cancelButtonTitle: "Não tenho interesse agora",
				selectText: "Selecione a quantidade de créditos:",
				termsFragment: `de ${selectedUpsellPlanAddon.quantity} minutos de ligação por ${convertCentsInReals(selectedUpsellPlanAddon.priceInCents)}`,
				formatAddonLabel: (addon) => {
					const pricePerMinuteInCents = addon.priceInCents / addon.quantity

					return {
						quantityLabel: `${addon.quantity} min`,
						unitPriceLabel: `(${convertCentsInReals(pricePerMinuteInCents)}/min)`
					}
				}
			}
		}

		return upsellQuantityTypeToContent[upsellQuantityType]
	}

	const upsellContent = getUpsellContent()

	return (
		<Grid
			container
			direction="column"
			className={classes.fullDialog}
		>
			<Grid
				item
			>
				<DialogTitle>
					<Grid
						container
						direction="column"
						spacing={2}
					>
						<Grid
							item
						>
							<Typography variant="h3" className={classes.dialogTitle}>
								{upsellContent.title}
							</Typography>
						</Grid>
					</Grid>
				</DialogTitle>
			</Grid>
			<Grid
				item
			>
				<DialogContent className={classes.dialogContent}>
					<Grid
						container
						direction="column"
						spacing={4}
					>
						<Grid
							item
						>
							<Typography className={classes.dialogDescription}>
								{upsellContent.description}
							</Typography>
						</Grid>

						<Grid
							item
						>
							<ul className={classes.benefitsList}>
								{upsellContent.benefits.map((benefit, index) => (
									<li key={index} className={classes.benefitItem}>
										<Avatar className={classes.benefitItemIconAvatar}>
											<Check className={classes.benefitItemIconInAvatar} />
										</Avatar>
										{benefit}
									</li>
								))}
							</ul>
						</Grid>

						<Grid
							item
						>
							<Grid
								container
								direction="column"
								spacing={2}
							>
								<Grid
									item
								>
									<Typography variant="h6" className={classes.planAddonsInformationText}>
										{upsellContent.selectText}
									</Typography>
								</Grid>

								<Grid
									item
								>
									<Paper className={classes.radioOptionsCard}>
										<RadioGroup
											value={selectedUpsellPlanAddon.planAddonId}
											onChange={(event) => onSelectPlanAddon(Number(event.target.value))}
										>
											{planAddons.map((addon) => {
												const isSelected = addon.planAddonId === selectedUpsellPlanAddon.planAddonId
												const addonLabel = upsellContent.formatAddonLabel(addon)

												return (
													<FormControlLabel
														key={addon.planAddonId}
														value={addon.planAddonId}
														control={<Radio color="primary" />}
														label={
															<Grid container alignItems="center" spacing={1}>
																<Grid item>
																	<Typography className={clsx(classes.radioOptionMinutes, { [classes.radioOptionMinutesSelected]: isSelected })}>
																		{addonLabel.quantityLabel}
																	</Typography>
																</Grid>
																<Grid item>
																	<Typography className={clsx(classes.radioOptionArrow, { [classes.radioOptionArrowSelected]: isSelected })}>→</Typography>
																</Grid>
																<Grid item>
																	<Typography className={clsx(classes.radioOptionPrice, { [classes.radioOptionPriceSelected]: isSelected })}>
																		{convertCentsInReals(addon.priceInCents)}
																	</Typography>
																</Grid>
																{addonLabel.unitPriceLabel && (
																	<Grid item>
																		<Typography className={clsx(classes.radioOptionPricePerMinute, { [classes.radioOptionPricePerMinuteSelected]: isSelected })}>
																			{addonLabel.unitPriceLabel}
																		</Typography>
																	</Grid>
																)}
															</Grid>
														}
													/>
												)
											})}
										</RadioGroup>
									</Paper>
								</Grid>
							</Grid>
						</Grid>

						<Grid
							item
						>
							<Grid
								container
								spacing={2}
							>
								<Grid
									item
								>
									<Grid
										container
										spacing={1}
									>
										<Grid
											item
											xs={10}
										>
											<Button
												variant="contained"
												endIcon={isCreatePlanAddonLoading ? <CircularProgress size={20} color={"inherit"} /> : null}
												className={classes.dialogActionPrimaryButton}
												disabled={isCreatePlanAddonLoading || !selectedUpsellPlanAddon?.planAddonId}
												onClick={onActivateClick}
												fullWidth
											>
												{upsellContent.confirmButtonTitle}
											</Button>
										</Grid>

										<Grid
											item
											xs={10}
										>
											<Typography
												className={classes.termsOfUse}
												variant="caption"
											>
												Ao continuar, você concorda com os <a className={classes.termsOfUseLink} href={letalkLinks.notionTermsOfService} target="_blank" rel="noopener noreferrer">termos de uso</a> e a contratação {upsellContent.termsFragment} que será cobrada de forma recorrente.
											</Typography>
										</Grid>
									</Grid>
								</Grid>

								<Grid
									item
									xs={10}
								>
									<Button
										variant="text"
										className={classes.dialogActionSecondaryButton}
										onClick={onCancelClick}
										fullWidth
									>
										{upsellContent.cancelButtonTitle}
									</Button>
								</Grid>
							</Grid>
						</Grid>
					</Grid>
				</DialogContent>
			</Grid>
		</Grid>
	)
}

export default UpsellQuantityDialogIdleContent
